import { useCallback, useState } from 'react'
import { Analytics } from '@genoa/analytics'
import { useOverwriteConfirm } from '@genoa/middle-end'

import { useAuthState } from '../../../../../../contexts'
import { useDefaultErrorModals } from '../../../../../../hooks/error-handling/use-default-error-modals'
import { useAccount } from '../../../../../../hooks/use-account'
import { useCustomerStatusState } from '../../../../../../modules/flex2/use-state'
import { useAnalytics, useLogger } from '../../../../../../providers'

export const useConfirmSchedule = () => {
  const analytics = useAnalytics()
  const { customerStatus } = useCustomerStatusState()
  const loggerV2 = useLogger('useConfirmSchedule')

  const [isConfirming, setIsConfirming] = useState(false)
  const { billerConnection } = useAccount()
  const [, overwriteConfirm] = useOverwriteConfirm()
  const { uid } = useAuthState()
  const { displayDefaultErrorModal, displayTooManyTriesModal } = useDefaultErrorModals()

  const submit = async (overwritePublicId: string, onConfirmSchedule: () => void) => {
    const billTransactionId = customerStatus?.bill_transaction_id
    const billerAccountPublicId = billerConnection?.biller_account_public_id

    try {
      setIsConfirming(true)
      if (!uid || !overwritePublicId || !billTransactionId || !billerAccountPublicId) {
        return
      }

      const { status } = await overwriteConfirm({
        billerAccountPublicId,
        billTransactionId,
        customerId: uid,
        overwritePublicId,
      })

      if (status === 204) {
        analytics.logEvent(Analytics.Events.OUT_OF_RANGE_PAYMENT_SCHEDULE_CONFIRM_SUCCESS)
        return onConfirmSchedule()
      } else if (status === 429) {
        analytics.logEvent(Analytics.Events.OUT_OF_RANGE_PAYMENT_SCHEDULE_CONFIRM_FAILURE)
        loggerV2.error('Coverage Failure useConfirmSchedule', 'Rate limit status code: 429')
        displayTooManyTriesModal()
      } else {
        analytics.logEvent(Analytics.Events.OUT_OF_RANGE_PAYMENT_SCHEDULE_CONFIRM_FAILURE)
        loggerV2.error('Coverage Failure useConfirmSchedule', `Non 204 status-code: ${status}`)
        displayDefaultErrorModal()
      }
    } catch (error) {
      const message = error instanceof Error ? error?.message : 'An unexpected error occured'
      loggerV2.error('Coverage Failure useConfirmSchedule', `Unhandled error: ${message}`)
      displayDefaultErrorModal()
    } finally {
      setIsConfirming(false)
    }
  }

  return {
    isConfirming,
    submit,
  }
}
